import React, { useRef, useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { getExploreUnknownMinigameEntry } from './exploreUnknownMinigameRegistry';
import {
  SHOW_BEYOND_BIRD_MINIGAME_GRID,
  getDefaultBeyondBirdTreeSlots,
} from './minigames/beyondBirdMemory/beyondBirdMemoryLayout';
import {
  loadDevBirdTreeSlots,
  saveDevBirdTreeSlots,
  clearDevBirdTreeSlots,
  formatBirdTreeSlotsForLayoutFile,
} from '../utils/exploreUnknownBirdTreeDevStorage';
import ExploreUnknownMinigameRewardPopupQueue from '../exploreUnknownRewardPopups/ExploreUnknownMinigameRewardPopupQueue';
import {
  getDefaultAutobahnLaneBoundaries,
  loadDevAutobahnLaneBoundaries,
  saveDevAutobahnLaneBoundaries,
  clearDevAutobahnLaneBoundaries,
  formatAutobahnLaneBoundariesForLayoutFile,
} from '../utils/exploreUnknownAutobahnLaneDevStorage';
import './ExploreUnknownMinigameHost.css';
import '../exploreUnknownMinigameMobile.css';

/**
 * Full-screen modal host for Explore Unknown map minigames.
 */
export default function ExploreUnknownMinigameHost({
  minigameId,
  challenge,
  open,
  onDismiss,
  onPickTree,
  completeBusy = false,
  completeError = null,
  completeOutcome = null,
  completeWinReward = null,
  completeWinRewards = null,
  completeGambleResolution = null,
  showPlayer1DevGrid = false,
  isMobile = false,
}) {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const [slotEditMode, setSlotEditMode] = useState(false);
  const [treeSlots, setTreeSlots] = useState(() => getDefaultBeyondBirdTreeSlots());
  const [slotDevStorageError, setSlotDevStorageError] = useState(null);
  const [slotDevUiError, setSlotDevUiError] = useState(null);
  const [slotCopyDone, setSlotCopyDone] = useState(false);
  const [rewardPopupsBlockingProceed, setRewardPopupsBlockingProceed] = useState(false);
  const [autobahnBoundaryEditMode, setAutobahnBoundaryEditMode] = useState(false);
  const [autobahnLaneBoundaries, setAutobahnLaneBoundaries] = useState(() => getDefaultAutobahnLaneBoundaries());
  const [autobahnBoundaryDevStorageError, setAutobahnBoundaryDevStorageError] = useState(null);
  const [autobahnBoundaryDevUiError, setAutobahnBoundaryDevUiError] = useState(null);
  const [autobahnBoundaryCopyDone, setAutobahnBoundaryCopyDone] = useState(false);

  const showBeyondBirdSlotEditor =
    process.env.NODE_ENV === 'development' &&
    showPlayer1DevGrid &&
    minigameId === 'beyondBirdMemory';

  const showAutobahnLaneBoundaryEditor =
    process.env.NODE_ENV === 'development' && showPlayer1DevGrid && minigameId === 'autobahnLaneSimon';

  useEffect(() => {
    if (!open || !showBeyondBirdSlotEditor) {
      return;
    }
    try {
      const stored = loadDevBirdTreeSlots();
      setTreeSlots(stored ?? getDefaultBeyondBirdTreeSlots());
      setSlotDevStorageError(null);
    } catch (err) {
      setSlotDevStorageError(err instanceof Error ? err.message : String(err));
      setTreeSlots(getDefaultBeyondBirdTreeSlots());
    }
  }, [open, showBeyondBirdSlotEditor]);

  useEffect(() => {
    if (!isMobile || typeof document === 'undefined') {
      return undefined;
    }
    if (open) {
      document.documentElement.classList.add('explore-unknown-minigame-mobile');
      return () => {
        document.documentElement.classList.remove('explore-unknown-minigame-mobile');
      };
    }
    return undefined;
  }, [isMobile, open]);

  useEffect(() => {
    if (!open) {
      setSlotEditMode(false);
      setSlotCopyDone(false);
      setSlotDevUiError(null);
      setRewardPopupsBlockingProceed(false);
      setAutobahnBoundaryEditMode(false);
      setAutobahnBoundaryCopyDone(false);
      setAutobahnBoundaryDevUiError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !showAutobahnLaneBoundaryEditor) {
      return;
    }
    try {
      const stored = loadDevAutobahnLaneBoundaries();
      setAutobahnLaneBoundaries(stored ?? getDefaultAutobahnLaneBoundaries());
      setAutobahnBoundaryDevStorageError(null);
    } catch (err) {
      setAutobahnBoundaryDevStorageError(err instanceof Error ? err.message : String(err));
      setAutobahnLaneBoundaries(getDefaultAutobahnLaneBoundaries());
    }
  }, [open, showAutobahnLaneBoundaryEditor]);

  const handleAutobahnBoundariesChange = useCallback((nextBoundaries) => {
    setAutobahnLaneBoundaries(nextBoundaries);
    try {
      saveDevAutobahnLaneBoundaries(nextBoundaries);
      setAutobahnBoundaryDevStorageError(null);
    } catch (err) {
      setAutobahnBoundaryDevStorageError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  const handleResetAutobahnLaneBoundaries = useCallback(() => {
    try {
      clearDevAutobahnLaneBoundaries();
      setAutobahnLaneBoundaries(getDefaultAutobahnLaneBoundaries());
      setAutobahnBoundaryDevUiError(null);
      setAutobahnBoundaryDevStorageError(null);
    } catch (err) {
      setAutobahnBoundaryDevUiError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  const handleCopyAutobahnLaneBoundaries = useCallback(async () => {
    const text = formatAutobahnLaneBoundariesForLayoutFile(autobahnLaneBoundaries);
    if (!navigator.clipboard?.writeText) {
      throw new Error('Clipboard API unavailable');
    }
    await navigator.clipboard.writeText(text);
    setAutobahnBoundaryCopyDone(true);
    window.setTimeout(() => setAutobahnBoundaryCopyDone(false), 2000);
  }, [autobahnLaneBoundaries]);

  const showRewardPopupQueue = open && completeOutcome === 'won';
  const canProceedAfterRewards =
    completeOutcome != null && !rewardPopupsBlockingProceed && !completeBusy;

  const handleSlotAuthoringChange = useCallback((index, center, hitRadius) => {
    if (process.env.NODE_ENV !== 'development') {
      throw new Error('Bird tree slot authoring is development-only');
    }
    setTreeSlots((prev) => {
      const next = prev.map((s) =>
        s.id === index ? { id: s.id, center: [...center], hitRadius } : { ...s, center: [...s.center] }
      );
      try {
        saveDevBirdTreeSlots(next);
        setSlotDevStorageError(null);
      } catch (err) {
        setSlotDevStorageError(err instanceof Error ? err.message : String(err));
      }
      return next;
    });
  }, []);

  const handleResetBirdTreeSlots = useCallback(() => {
    try {
      clearDevBirdTreeSlots();
      setTreeSlots(getDefaultBeyondBirdTreeSlots());
      setSlotDevUiError(null);
      setSlotDevStorageError(null);
    } catch (err) {
      setSlotDevUiError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  const handleCopyBirdTreeSlots = useCallback(async () => {
    const text = formatBirdTreeSlotsForLayoutFile(treeSlots);
    if (!navigator.clipboard?.writeText) {
      throw new Error('Clipboard API unavailable');
    }
    await navigator.clipboard.writeText(text);
    setSlotCopyDone(true);
    window.setTimeout(() => setSlotCopyDone(false), 2000);
  }, [treeSlots]);

  if (!open || minigameId == null) {
    return null;
  }

  const entry = getExploreUnknownMinigameEntry(minigameId);
  const GameComponent = entry.Component;
  const isWombafleksCatch = minigameId === 'wombafleksCatch';
  const isRewardMemory = minigameId === 'rewardMemory';
  const isBalloonBoothPop = minigameId === 'balloonBoothPop';
  const isTheGamble = minigameId === 'theGamble';
  const isSpottingObjects = minigameId === 'spottingObjects';
  const isAutobahnLaneSimon = minigameId === 'autobahnLaneSimon';
  const isPixelPastRescue = minigameId === 'pixelPastRescue';
  const isBoatSharkCrossing = minigameId === 'boatSharkCrossing';
  const sceneOverlayModal =
    isWombafleksCatch ||
    isRewardMemory ||
    isBalloonBoothPop ||
    isTheGamble ||
    isSpottingObjects ||
    isAutobahnLaneSimon ||
    isPixelPastRescue ||
    isBoatSharkCrossing;
  const showDevGrid =
    showBeyondBirdSlotEditor &&
    minigameId === 'beyondBirdMemory' &&
    (slotEditMode || SHOW_BEYOND_BIRD_MINIGAME_GRID);

  const devBar =
    showBeyondBirdSlotEditor && typeof document !== 'undefined'
      ? createPortal(
          <div
            className="explore-unknown-bird-tree-slot-dev-bar"
            role="region"
            aria-label="Development bird tree hole editor"
          >
            <span className="explore-unknown-bird-tree-slot-dev-bar-title">Dev: tree holes</span>
            <button
              type="button"
              className="explore-unknown-bird-tree-slot-dev-bar-button"
              onClick={() => setSlotEditMode((v) => !v)}
            >
              {slotEditMode ? 'Stop editing' : 'Drag / resize holes'}
            </button>
            <button
              type="button"
              className="explore-unknown-bird-tree-slot-dev-bar-button"
              onClick={() => handleResetBirdTreeSlots()}
            >
              Reset to layout file
            </button>
            <button
              type="button"
              className="explore-unknown-bird-tree-slot-dev-bar-button"
              onClick={() => {
                void handleCopyBirdTreeSlots().catch((err) => {
                  setSlotDevUiError(err instanceof Error ? err.message : String(err));
                });
              }}
            >
              {slotCopyDone ? 'Copied' : 'Copy slots for layout file'}
            </button>
            <span className="explore-unknown-bird-tree-slot-dev-bar-hint">localStorage · drag center · yellow handle = size</span>
            {slotDevStorageError ? (
              <span className="explore-unknown-bird-tree-slot-dev-bar-error" role="alert">
                {slotDevStorageError}
              </span>
            ) : null}
            {slotDevUiError ? (
              <span className="explore-unknown-bird-tree-slot-dev-bar-error" role="alert">
                {slotDevUiError}
              </span>
            ) : null}
          </div>,
          document.body
        )
      : null;

  const autobahnDevBar =
    showAutobahnLaneBoundaryEditor && typeof document !== 'undefined'
      ? createPortal(
          <div
            className="explore-unknown-bird-tree-slot-dev-bar explore-unknown-autobahn-lane-dev-bar"
            role="region"
            aria-label="Development Autobahn lane boundary editor"
          >
            <span className="explore-unknown-bird-tree-slot-dev-bar-title">Dev: lane boundaries</span>
            <button
              type="button"
              className="explore-unknown-bird-tree-slot-dev-bar-button"
              onClick={() => setAutobahnBoundaryEditMode((v) => !v)}
            >
              {autobahnBoundaryEditMode ? 'Stop editing' : 'Drag boundary lines'}
            </button>
            <button
              type="button"
              className="explore-unknown-bird-tree-slot-dev-bar-button"
              onClick={() => handleResetAutobahnLaneBoundaries()}
            >
              Reset to layout file
            </button>
            <button
              type="button"
              className="explore-unknown-bird-tree-slot-dev-bar-button"
              onClick={() => {
                void handleCopyAutobahnLaneBoundaries().catch((err) => {
                  setAutobahnBoundaryDevUiError(err instanceof Error ? err.message : String(err));
                });
              }}
            >
              {autobahnBoundaryCopyDone ? 'Copied' : 'Copy for layout file'}
            </button>
            <span className="explore-unknown-bird-tree-slot-dev-bar-hint">
              Drag handles 0–4 at road bottom · yellow = dividers · colored fills = lanes 1–4
            </span>
            {autobahnBoundaryDevStorageError ? (
              <span className="explore-unknown-bird-tree-slot-dev-bar-error" role="alert">
                {autobahnBoundaryDevStorageError}
              </span>
            ) : null}
            {autobahnBoundaryDevUiError ? (
              <span className="explore-unknown-bird-tree-slot-dev-bar-error" role="alert">
                {autobahnBoundaryDevUiError}
              </span>
            ) : null}
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <div
        className={`explore-unknown-minigame-modal-overlay${
          isMobile ? ' explore-unknown-minigame-modal-overlay--mobile' : ''
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={sceneOverlayModal ? undefined : 'explore-unknown-minigame-title'}
      >
        <div
          className={
            sceneOverlayModal
              ? `explore-unknown-minigame-modal explore-unknown-minigame-modal--scene-overlay${
                  isMobile ? ' explore-unknown-minigame-modal--mobile' : ''
                }${isWombafleksCatch ? ' explore-unknown-minigame-modal--wombafleks' : ''}${
                  isRewardMemory ? ' explore-unknown-minigame-modal--reward-memory' : ''
                }${isBalloonBoothPop ? ' explore-unknown-minigame-modal--balloon-booth' : ''}${
                  isTheGamble ? ' explore-unknown-minigame-modal--the-gamble' : ''
                }${isSpottingObjects ? ' explore-unknown-minigame-modal--spotting-objects' : ''}${
                  isAutobahnLaneSimon ? ' explore-unknown-minigame-modal--autobahn-lane-simon' : ''
                }${isBoatSharkCrossing ? ' explore-unknown-minigame-modal--boat-shark-crossing' : ''}`
              : `explore-unknown-minigame-modal${isMobile ? ' explore-unknown-minigame-modal--mobile' : ''}`
          }
        >
          {sceneOverlayModal ? (
            <div className="explore-unknown-minigame-modal-close-host">
              <button
                type="button"
                className="explore-unknown-minigame-modal-close"
                disabled={completeBusy || completeOutcome != null}
                onClick={() => onDismiss()}
                aria-label={t('exploreUnknownPage.minigame.closeAria')}
              >
                ×
              </button>
            </div>
          ) : (
            <div className="explore-unknown-minigame-modal-header">
              <h2 id="explore-unknown-minigame-title" className="explore-unknown-minigame-modal-title">
                {t(entry.titleI18nKey)}
              </h2>
              <button
                type="button"
                className="explore-unknown-minigame-modal-close"
                disabled={completeBusy || completeOutcome != null}
                onClick={() => onDismiss()}
                aria-label={t('exploreUnknownPage.minigame.closeAria')}
              >
                ×
              </button>
            </div>
          )}
          {completeError ? (
            <p className="explore-unknown-minigame-modal-error" role="alert">
              {completeError}
            </p>
          ) : null}
          <div className="explore-unknown-minigame-modal-body">
            <GameComponent
              challenge={challenge}
              onPickTree={onPickTree}
              disabled={
                completeBusy ||
                slotEditMode ||
                autobahnBoundaryEditMode ||
                completeOutcome != null
              }
              showDevGrid={showDevGrid}
              containerRef={containerRef}
              treeSlots={showBeyondBirdSlotEditor ? treeSlots : undefined}
              slotEditMode={showBeyondBirdSlotEditor && slotEditMode}
              onSlotAuthoringChange={showBeyondBirdSlotEditor ? handleSlotAuthoringChange : undefined}
              laneBoundaryXBottom={showAutobahnLaneBoundaryEditor ? autobahnLaneBoundaries : undefined}
              boundaryEditMode={showAutobahnLaneBoundaryEditor && autobahnBoundaryEditMode}
              onBoundaryAuthoringChange={
                showAutobahnLaneBoundaryEditor ? handleAutobahnBoundariesChange : undefined
              }
              resultOutcome={completeOutcome}
              winReward={completeWinReward}
              winRewards={completeWinRewards}
              gambleResolution={completeGambleResolution}
              onResultProceed={canProceedAfterRewards ? onDismiss : undefined}
            />
          </div>
        </div>
      </div>
      <ExploreUnknownMinigameRewardPopupQueue
        active={showRewardPopupQueue}
        winRewards={completeWinRewards}
        winReward={completeWinReward}
        onBlockingChange={setRewardPopupsBlockingProceed}
      />
      {devBar}
      {autobahnDevBar}
    </>
  );
}
